/**
 * Device detection utilities for PrintBoard
 * Determines if user is on a mobile/tablet device
 */

/**
 * Detects true phone-class devices from user agent signals only.
 * This is used for routes where narrow desktop windows must remain desktop.
 *
 * @returns true if device is a phone, false otherwise
 */
export function isPhoneDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }

  const userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    (window as Window & { opera?: string }).opera ||
    ''

  return /iphone|ipod|android.*mobile|windows phone|iemobile|opera mini|mobile/i.test(
    userAgent.toLowerCase()
  )
}

/**
 * Detects if the current device is a mobile or tablet device
 * Uses both user agent detection and screen width as fallback
 *
 * @returns true if device is mobile/tablet, false if desktop
 */
export function isMobileDevice(): boolean {
  // SSR safety check
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }

  const userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    (window as Window & { opera?: string }).opera ||
    ''

  // Check for mobile/tablet user agents
  const mobileRegex =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase())

  // Check screen width as fallback (tablets and small screens)
  // PrintBoard canvas requires minimum ~1200px for comfortable editing
  const isSmallScreen = window.innerWidth < 1024

  return isMobileUA || isSmallScreen
}

/**
 * Gets device type as a string for analytics/debugging
 *
 * @returns 'mobile' | 'tablet' | 'desktop'
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') {
    return 'desktop'
  }

  const userAgent = navigator.userAgent.toLowerCase()
  const width = window.innerWidth

  if (isPhoneDevice()) {
    return 'mobile'
  }

  // Mobile phones (typically < 768px)
  if (width < 768) {
    return 'mobile'
  }

  // Tablets (768px - 1024px)
  if (
    /ipad|android(?!.*mobile)|tablet/.test(userAgent) ||
    (width >= 768 && width < 1024)
  ) {
    return 'tablet'
  }

  return 'desktop'
}
